import { useState } from "react";
import { SvgIcon } from "@progress/kendo-react-common";
import {
  checkCircleIcon,
  xCircleIcon,
  clockIcon,
} from "@progress/kendo-svg-icons";
import { ICON_BASE_PATH } from "@/app/utils/config";
import { AnimatePresence, motion } from "framer-motion";
import type { ApprovalProgressProps } from "@/app/types/beneficiaries";
import { gxDateTimeFormat } from "@/app/utils/helpers/gxDateTimeFormat";

// ============================================================================
// CENTRALIZED STATUS CODE MAP
// ============================================================================

export const STATUS_CODE_MAP: Record<string, string> = {
  "3000": "Beneficiary Approved",
  "3001": "Beneficiary Self Approved",
  "3015": "Beneficiary Awaiting Approval",
  "3020": "Beneficiary Rejected",
};

export const getStatusLabelFromCode = (statusCode: string): string =>
  STATUS_CODE_MAP[statusCode] ?? "Pending Approval";

// ============================================================================
// OVERALL STATUS HELPERS - derived from recordStatusCode only
// ============================================================================

const getOverallStatusFromCode = (statusCode: string): "A" | "R" | "P" => {
  switch (statusCode) {
    case "3000": // Approved
    case "3001": // Self-approved
      return "A";
    case "3020": // Rejected
      return "R";
    case "3015": // Awaiting approval
    default:
      return "P";
  }
};

// ============================================================================
// APPROVAL STATUS HELPERS
// ============================================================================

const mapApprovedStatusToLetter = (status: string): "A" | "R" | "P" => {
  if (status === "3000" || status === "3001") return "A";
  if (status === "3020") return "R";
  if (status === "3015") return "P";

  const s = status?.toUpperCase();
  if (s === "APPROVED" || s === "SELFAPPROVED" || s === "SELF_APPROVED")
    return "A";
  if (s === "REJECTED") return "R";
  return "P";
};

// ============================================================================
// VISUAL HELPERS (Icon, Color, Label)
// ============================================================================

const getStepImgIcon = (status: string): string => {
  switch (status) {
    case "A":
      return `${ICON_BASE_PATH}green_tick.svg`;
    case "R":
      return `${ICON_BASE_PATH}failed-icon.svg`;
    case "P":
      return `${ICON_BASE_PATH}waiting.svg`;
    default:
      return `${ICON_BASE_PATH}waiting.svg`;
  }
};

const getStepSvgIcon = (status: string) => {
  switch (status) {
    case "A":
      return checkCircleIcon;
    case "R":
      return xCircleIcon;
    default:
      return clockIcon;
  }
};

const getStepIconColor = (status: string): string => {
  switch (status) {
    case "A":
      return "#16a34a";
    case "R":
      return "#ef4444";
    case "P":
      return "#fb923c";
    default:
      return "#6b7280";
  }
};

const getStepStatusLabel = (status: string): string => {
  switch (status) {
    case "A":
      return "Approved";
    case "R":
      return "Rejected";
    case "P":
      return "Pending";
    default:
      return "-";
  }
};

const getStepLineClass = (status: string): string => {
  switch (status) {
    case "A":
      return "payment-progress__line payment-progress__line--ok";
    case "R":
      return "payment-progress__line payment-progress__line--error";
    case "P":
      return "payment-progress__line payment-progress__line--warn";
    default:
      return "payment-progress__line";
  }
};

const getStatusCssClass = (statusCode: string): string => {
  switch (statusCode) {
    case "3000":
    case "3001":
      return "status-approvedss";
    case "3020":
      return "bene-status-failed";
    case "3015":
      return "status-awaiting";
    default:
      return "";
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ApprovalProgress: React.FC<ApprovalProgressProps> = ({ data }) => {
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(
    new Set([0, 1]),
  );

  if (!data) return null;

  const currentStepStatus = getOverallStatusFromCode(data.recordStatusCode);
  const formattedStatusLabel = getStatusLabelFromCode(data.recordStatusCode);

  // === Extract approval counts =====================================================================
  const approvalsRequired =
    data.beneMetromapDto?.approvalDetails?.approvalsRequired ??
    data.approvalsRequired ??
    data.noOfApprLevel ??
    0;

  const approvalsCompleted =
    data.beneMetromapDto?.approvalDetails?.approvalsCompleted ??
    data.approvalsCompleted ??
    0;

  // === Build approval history ======================================================================
  let approvalHistory: Array<{
    approverName: string;
    approvalDate: string;
    status: "A" | "R" | "P";
    remarks?: string;
  }> = [];

  if (data.beneMetromapDto?.approvalDetails?.approvers) {
    approvalHistory = data.beneMetromapDto.approvalDetails.approvers.map(
      (approver) => ({
        approverName: approver.approvedName || "",
        approvalDate: approver.approvedAt
          ? gxDateTimeFormat(approver.approvedAt)
          : "",
        status: mapApprovedStatusToLetter(approver.approvedStatus) as
          | "A"
          | "R"
          | "P",
        remarks: approver.reason || "",
      }),
    );
  } else if (Array.isArray(data.authzHists)) {
    approvalHistory = data.authzHists.map((hist) => ({
      approverName: hist.aprdByName ?? "",
      approvalDate: gxDateTimeFormat(hist.aprdDt),
      status: (hist.aprdStatus ?? "P") as "A" | "R" | "P",
      remarks: hist.remarks || "",
    }));
  }

  // Fill missing approval levels as pending
  const totalLevels = approvalsRequired || approvalHistory.length;
  const filledApprovalHistory = [
    ...approvalHistory,
    ...Array(Math.max(0, totalLevels - approvalHistory.length)).fill({
      approverName: "",
      approvalDate: "",
      status: "P",
      remarks: "",
    }),
  ];

  const toggleStep = (index: number) => {
    setExpandedSteps((prev) => {
      const copy = new Set(prev);
      copy.has(index) ? copy.delete(index) : copy.add(index);
      return copy;
    });
  };

  return (
    <div className="col-span-12 gx-row-align-center">
      <div className="gx-approval-progress">
        <ul className="gx-approval-timeline">
          {/* Beneficiary Approval Initiated */}
          <li className="payment-progress__step">
            <div className="payment-progress__icon-col">
              <SvgIcon
                icon={getStepSvgIcon("A")}
                style={{ color: getStepIconColor("A") }}
                className="payment-progress__icon"
              />
              <span className={getStepLineClass("A")} />
            </div>

            <div className="gx-step-connect">
              <div
                className="gx-approval-initiated"
                onClick={() => toggleStep(0)}
              >
                <h4 className="gx-ape-completed">
                  <span className="gx-p14-md">
                    Beneficiary Approval Initiated
                  </span>
                  <span className="gx-crtdDt gx-p10">
                    {data.beneMetromapDto?.initiatedDetails?.initiatedAt
                      ? gxDateTimeFormat(
                          data.beneMetromapDto.initiatedDetails.initiatedAt,
                        )
                      : gxDateTimeFormat(data.crtdDt)}
                  </span>
                </h4>
              </div>

              <AnimatePresence>
                {expandedSteps.has(0) && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="gx-approval-card"
                  >
                    <div className="gx-approval-created">
                      <p className="gx-p10">Created by:</p>
                      <p className="gx-p12-m">
                        {data.beneMetromapDto?.initiatedDetails?.initiatedBy ||
                          data.crtdName}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </li>

          {/* Current Status - label derived from recordStatusCode only */}
          <li className="payment-progress__step">
            <div className="payment-progress__icon-col">
              <SvgIcon
                icon={getStepSvgIcon(currentStepStatus)}
                style={{ color: getStepIconColor(currentStepStatus) }}
                className="payment-progress__icon"
              />
            </div>

            <div className="gx-step-connect">
              <div
                className="gx-approval-initiated"
                onClick={() => toggleStep(1)}
              >
                <h4 className="gx-ape-completed gx-p14-md">
                  {/* === Status label is now always from STATUS_CODE_MAP === */}
                  <div className={getStatusCssClass(data.recordStatusCode)}>
                    <span className="gx-p12-md">{formattedStatusLabel}</span>
                  </div>
                  <span className="gx-crtdDt gx-p10">
                    {data.beneMetromapDto?.initiatedDetails?.initiatedAt
                      ? gxDateTimeFormat(
                          data.beneMetromapDto.initiatedDetails.initiatedAt,
                        )
                      : gxDateTimeFormat(data.crtdDt)}
                  </span>
                </h4>
              </div>

              <AnimatePresence>
                {expandedSteps.has(1) && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="gx-approval-card"
                  >
                    <div className="gx-approval-created gx-p12-m payment-progress__approval-list">
                      <span className="gx-p12-m payment-progress__info-label">
                        {approvalsCompleted} of {approvalsRequired} Approvals
                        Completed
                      </span>

                      {/* Approvers List */}
                      <ul className="gx-approvals-list">
                        {filledApprovalHistory?.map((step, idx) => (
                          <li
                            key={idx}
                            className="gx-approval-list-item gx-p12-m"
                          >
                            <img
                              src={getStepImgIcon(step.status)}
                              alt={getStepStatusLabel(step.status)}
                              className="gx-icon"
                            />
                            <span className="gx-approver-name gx-p12-m">
                              {step.approverName || `Approver Level ${idx + 1}`}
                            </span>
                            {step.status !== "P" && step.approvalDate && (
                              <span className="gx-approval-date gx-p10-r">
                                {step.approvalDate}
                              </span>
                            )}
                            <span
                              className="payment-progress__status"
                              style={{ color: getStepIconColor(step.status) }}
                            />
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default ApprovalProgress;
